import { useState } from "react";
import type { ExternalUserFormData } from "@/schemas/users/external-user";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Heading,
  Icon,
  Switch,
} from "@elaniintc/goes-design-system";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import axios from "@/utils/axios";
import IconWrapper from "../components/icon-wrapper";
import Insolvencies from "../actions/insolvencies";
import {
  AssociationsListModal,
  type AssociationItem,
} from "@/pages/users/components/associations-list-modal";
import { useUnlinkAssociation } from "@/queries/associations/associations.api";
import { UnlinkAssociationModal } from "@/pages/users/components/unlink-association-modal/unlink-association-modal";
import { isLegalPersonDocument } from "@/utils/document-types";

interface Props {
  selectedUserType: {
    label: string;
    value: string;
  } | null;
  isUserActive: boolean;
  isUserBlocked?: boolean;
  registrationPhase?: string;
  toggleUserActivation: (isActive: boolean) => void;
  onResetPassword: () => void;
  onUnblockUser?: () => void;
  userId: string;
}

const ExternalUserDetails = ({
  selectedUserType,
  isUserActive,
  isUserBlocked = false,
  registrationPhase,
  toggleUserActivation,
  onResetPassword,
  onUnblockUser,
  userId,
}: Props) => {
  const { watch } = useFormContext<ExternalUserFormData>();
  const data = watch();

  const { data: associationsData } = useQuery({
    queryKey: ["user-associations", userId],
    queryFn: async () => {
      const response = await axios.get(
        `/admin/v1/applicant/${userId}/associations`,
      );
      return response.data;
    },
    enabled: !!userId,
  });

  const representatives = associationsData?.data?.representatives ?? [];
  const representations = associationsData?.data?.representations ?? [];

  const [associationsModal, setAssociationsModal] = useState<
    "representatives" | "representations" | null
  >(null);

  // JER-453 — unlink: close the list modal, open the unlink modal, and reopen
  // the list on cancel/success.
  const unlinkMutation = useUnlinkAssociation();
  const [unlinkTarget, setUnlinkTarget] = useState<{
    item: AssociationItem;
    from: "representatives" | "representations";
  } | null>(null);

  const handleUnlinkRequest = (item: AssociationItem) => {
    if (!associationsModal) return;
    setUnlinkTarget({ item, from: associationsModal });
    setAssociationsModal(null);
  };

  const closeUnlinkAndReopenList = () => {
    if (unlinkTarget) setAssociationsModal(unlinkTarget.from);
    setUnlinkTarget(null);
  };

  const handleConfirmUnlink = (reason: string) => {
    if (!unlinkTarget) return;
    unlinkMutation.mutate(
      { id: unlinkTarget.item.id, reason },
      { onSuccess: closeUnlinkAndReopenList },
    );
  };

  const unlinkPerson =
    unlinkTarget?.from === "representatives"
      ? unlinkTarget.item.representative
      : unlinkTarget?.item.represented;
  const unlinkPersonName = unlinkPerson
    ? unlinkPerson.commercial_name ||
      `${unlinkPerson.first_name} ${unlinkPerson.last_name || ""}`.trim()
    : "";

  const isLegalPerson = isLegalPersonDocument(data.document_type);
  const showLastName = !isLegalPerson;

  const fullName = isLegalPerson
    ? data.commercial_name
      ? `${data.first_name || ""} (${data.commercial_name})`.trim()
      : data.first_name || ""
    : showLastName
      ? `${data.first_name || ""} ${data.last_name || ""}`.trim()
      : data.first_name || "";

  return (
    <div className="p-4">
      <div className="flex flex-col gap-4 pb-4">
        <Avatar size="xl">
          <AvatarFallback color="action">
            {data.first_name?.charAt(0) || data.last_name?.charAt(0) || ""}
          </AvatarFallback>
        </Avatar>
        <div>
          <Heading
            variant="h5"
            className={cn("text-on-disabled", fullName && "text-heading")}
          >
            {fullName ||
              (isLegalPerson
                ? "Razón social (Nombre comercial)"
                : "Nombre y apellido")}
          </Heading>
          <p
            className={cn(
              "text-base leading-3xl text-body-quiet",
              selectedUserType && "text-on-disabled",
            )}
          >
            {selectedUserType?.label || "Tipo de usuario"}
          </p>
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-2 py-4">
        <div className="flex items-center gap-3">
          <IconWrapper>
            <Icon type="far" name="id-card" />
          </IconWrapper>
          <div className="flex flex-col gap-1">
            <span
              className={cn(
                "text-body font-normal",
                !data.document_number && "text-on-disabled",
              )}
              data-testid="profile-document"
            >
              {data.document_number || "Número de documento"}
            </span>
          </div>
        </div>
        {!isLegalPerson && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconWrapper>
                <Icon type="far" name="birthday-cake" />
              </IconWrapper>
              <span
                className={cn(
                  "text-body font-normal",
                  !data.birthdate && "text-on-disabled",
                )}
                data-testid="profile-birthdate"
              >
                {data.birthdate
                  ? (() => {
                      try {
                        const date = new Date(data.birthdate);
                        if (!Number.isNaN(date.getTime())) {
                          return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
                        }
                      } catch {
                        return data.birthdate;
                      }
                      return data.birthdate;
                    })()
                  : "Fecha de nacimiento"}
              </span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="envelope" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !data.email && "text-on-disabled",
              )}
              data-testid="profile-email"
            >
              {data.email || "Correo electrónico"}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="phone" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !data.phone && "text-on-disabled",
              )}
              data-testid="profile-phone"
            >
              {data.phone.number
                ? `+(${data.phone.calling_code}) ${data.phone.number}`
                : "Número de teléfono"}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-2 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="users" />
            </IconWrapper>
            <span className="text-body font-normal">
              Representantes: <strong>{representatives.length}</strong>
            </span>
          </div>
          <button
            type="button"
            className="text-sm text-action flex items-center gap-1 hover:underline"
            onClick={() => setAssociationsModal("representatives")}
          >
            Ver detalle
            <Icon type="far" name="arrow-up-right" className="text-xs" />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="users" />
            </IconWrapper>
            <span className="text-body font-normal">
              Representaciones: <strong>{representations.length}</strong>
            </span>
          </div>
          <button
            type="button"
            className="text-sm text-action flex items-center gap-1 hover:underline"
            onClick={() => setAssociationsModal("representations")}
          >
            Ver detalle
            <Icon type="far" name="arrow-up-right" className="text-xs" />
          </button>
        </div>
      </div>
      <div className="w-full">
        <div className="flex flex-col justify-between">
          <Insolvencies userName={fullName || "Usuario"} userId={userId} />
        </div>
      </div>
      <div className="w-full h-px" />
      <div className="flex flex-col gap-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="user-check" />
            </IconWrapper>
            <span className="text-body-loud text-base leading-3xl font-semibold">
              Estado
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            {registrationPhase === "PRE_REGISTERED" ? (
              <Badge size="xs" variant="warning">
                Pendiente de registro en el sistema
                <Icon
                  type="fas"
                  name="triangle-exclamation"
                  className="ml-1 text-xs"
                />
              </Badge>
            ) : (
              <>
                <label
                  htmlFor="profile-status-switch"
                  className="text-body text-base leading-3xl"
                >
                  {isUserActive ? "Activo" : "Inactivo"}
                </label>
                <Switch
                  checked={isUserActive}
                  id="profile-status-switch"
                  onCheckedChange={(checked) => {
                    toggleUserActivation(checked);
                  }}
                  data-testid="profile-status-switch"
                  className="border-none!"
                />
              </>
            )}
          </div>
        </div>
        <Button
          size="default"
          variant="tertiary"
          className="w-full"
          type="button"
          onClick={onResetPassword}
        >
          Restablecer contraseña
          <Icon type="far" name="lock" className="text-current" />
        </Button>
        <Button
          size="default"
          variant="tertiary"
          className="w-full"
          type="button"
          disabled={!isUserBlocked}
          onClick={onUnblockUser}
        >
          Desbloquear usuario
          <Icon type="far" name="user-unlock" className="text-current" />
        </Button>
      </div>

      <AssociationsListModal
        isOpen={associationsModal === "representatives"}
        onClose={() => setAssociationsModal(null)}
        title="Representantes"
        items={representatives}
        direction="representative"
        onUnlink={handleUnlinkRequest}
      />
      <AssociationsListModal
        isOpen={associationsModal === "representations"}
        onClose={() => setAssociationsModal(null)}
        title="Representaciones"
        items={representations}
        direction="represented"
        onUnlink={handleUnlinkRequest}
      />

      {unlinkTarget && unlinkPerson && (
        <UnlinkAssociationModal
          isOpen
          onClose={closeUnlinkAndReopenList}
          onConfirm={handleConfirmUnlink}
          isLoading={unlinkMutation.isPending}
          title={
            unlinkTarget.from === "representatives"
              ? "Desvincular representante"
              : "Desvincular representación"
          }
          description={
            unlinkTarget.from === "representatives" ? (
              <>
                Al desvincular, este representante ya no podrá hacer trámites en
                nombre de{" "}
                <span className="font-semibold text-body-loud">{fullName}</span>
                .
              </>
            ) : (
              <>
                Al desvincular, {fullName} ya no podrá hacer trámites en nombre
                de{" "}
                <span className="font-semibold text-body-loud">
                  {unlinkPersonName}
                </span>
                .
              </>
            )
          }
          person={{
            name: unlinkPersonName,
            email: unlinkPerson.email,
            statusLabel: "Activo",
            associatedSince:
              unlinkTarget.item.authorization_approved_at ||
              unlinkTarget.item.created_at,
          }}
        />
      )}
    </div>
  );
};

export default ExternalUserDetails;
